import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SEO from "../components/SEO";
import { FaCheckCircle, FaTruck, FaMotorcycle, FaRoad, FaTools } from "react-icons/fa";
import "./Recovery.css";
import heroImage from "../assets/VDR.webp";
import Footer from "../components/Footer";

const Recovery = () => {
  const [selectedServices, setSelectedServices] = useState([]);
  const navigate = useNavigate();

  const services = [
    { id: 1, name: "Emergency Breakdown Assistance", description: "24/7 emergency callout for vehicle breakdowns anywhere in London & Essex", category: "Roadside Assistance" },
    { id: 2, name: "Jump Start Service", description: "Battery jump start service to get you moving again quickly", category: "Roadside Assistance" },
    { id: 3, name: "Flat Tyre Change", description: "Roadside tyre change service with spare wheel fitting", category: "Roadside Assistance" },
    { id: 4, name: "Fuel Delivery", description: "Emergency fuel delivery when you run out of petrol or diesel", category: "Roadside Assistance" },
    { id: 5, name: "Lockout Service", description: "Vehicle lockout assistance to help you gain access to your car", category: "Roadside Assistance" },

    { id: 6, name: "Accident Recovery", description: "Professional recovery service for vehicles involved in accidents", category: "Vehicle Recovery" },
    { id: 7, name: "Breakdown Recovery", description: "Complete vehicle recovery for mechanical failures and breakdowns", category: "Vehicle Recovery" },
    { id: 8, name: "Long Distance Recovery", description: "Recovery service for long distance transportation across UK", category: "Vehicle Recovery" },
    { id: 9, name: "Home to Garage Recovery", description: "Safe transportation of your vehicle from home to your chosen garage", category: "Vehicle Recovery" },
    { id: 10, name: "Insurance Recovery", description: "Recovery services for insurance claims and write-offs", category: "Vehicle Recovery" },

    { id: 11, name: "Motorcycle Breakdown Recovery", description: "Specialized recovery service for motorcycles and scooters", category: "Motorcycle Recovery" },
    { id: 12, name: "Bike Transport", description: "Safe and secure motorcycle transportation service", category: "Motorcycle Recovery" },
    { id: 13, name: "Track Day Recovery", description: "Recovery service for track bikes and racing motorcycles", category: "Motorcycle Recovery" },

    { id: 14, name: "Vehicle Relocation", description: "Professional vehicle relocation and moving services", category: "Vehicle Transportation" },
    { id: 15, name: "Auction Transport", description: "Transportation service for auction purchases and sales", category: "Vehicle Transportation" },
    { id: 16, name: "Showroom Delivery", description: "Delivery service for dealerships and showrooms", category: "Vehicle Transportation" },
    { id: 17, name: "Private Sale Collection", description: "Collection service for private vehicle purchases", category: "Vehicle Transportation" },
    { id: 18, name: "Fleet Transportation", description: "Multi-vehicle transportation for fleet operators", category: "Vehicle Transportation" },
  ];

  const handleToggle = (service) => {
    const isSelected = selectedServices.find((s) => s.id === service.id);
    if (isSelected) setSelectedServices(selectedServices.filter((s) => s.id !== service.id));
    else setSelectedServices([...selectedServices, service]);
  };

  const handleRequestRecovery = () => {
    if (selectedServices.length === 0) return;

    const serviceNames = selectedServices.map(service => service.name).join(", ");
    const subject = `Vehicle Recovery Service Request - ${serviceNames}`;
    const message = `Hello,\n\nI need the following recovery services:\n\n${selectedServices.map(service => `• ${service.name} (${service.category})\n  ${service.description}`).join('\n\n')}\n\nWhat I require:\n- Fast response time\n- Professional and safe vehicle handling\n- Competitive pricing\n- Fully insured recovery service\n\nPlease provide me with information about your recovery service, pricing, and availability for immediate assistance.\n\nThank you!`;

    navigate("/contact", { state: { subject, message } });
  };

  const groupedServices = services.reduce((acc, service) => {
    if (!acc[service.category]) acc[service.category] = [];
    acc[service.category].push(service);
    return acc;
  }, {});

  const canonical = "https://carforensic.uk/recovery";

  const recoverySchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Vehicle Recovery and Roadside Assistance",
    "url": canonical,
    "areaServed": ["Kent", "Maidstone", "Gillingham", "Sevenoaks", "Sittingbourne"],
    "provider": {
      "@type": "AutoRepair",
      "name": "Car Forensics LTD",
      "url": "https://carforensic.uk/",
      "telephone": "+447501911970",
      "email": "carforensicsltd@gmail.com"
    },
    "description":
      "Vehicle recovery and roadside assistance with fast response across Kent including Maidstone, Gillingham, Sevenoaks and Sittingbourne."
  };

  return (
    <>
      <SEO
        title="Car Forensics LTD | Recovery"
        description="Vehicle recovery and roadside assistance across Kent — including Maidstone, Gillingham, Sevenoaks and Sittingbourne. Select the help you need and contact Car Forensics LTD for fast support."
        canonical={canonical}
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(recoverySchema)}
        </script>
      </Helmet>

      <div className="recovery-page">
        <section className="recovery-hero" style={{ backgroundImage: `url(${heroImage})` }}>
          <div className="heros-overlay"></div>
          <div className="recovery-hero-content">
            <div className="hero-icon-wrapper">
              <FaTruck className="hero-icon" />
            </div>
            <h1>Vehicle Recovery Services</h1>
            <p>24/7 breakdown recovery and vehicle transportation across London & Essex</p>
            <div className="hero-stats">
              <div className="stat-item">
                <FaRoad />
                <span>Fast Response</span>
              </div>
              <div className="stat-item">
                <FaTruck />
                <span>Professional Recovery</span>
              </div>
              <div className="stat-item">
                <FaCheckCircle />
                <span>Fully Insured</span>
              </div>
            </div>
          </div>
        </section>

        <section className="recovery-section">
          <div className="recovery-container">
            <div className="section-intro">
              <h2>Select Recovery Services</h2>
              <p>Choose from our comprehensive range of vehicle recovery and roadside assistance services.</p>
            </div>

            <div className="recovery-content-wrapper">
              <div className="services-selection-area">
                {Object.entries(groupedServices).map(([category, categoryServices]) => (
                  <div key={category} className="category-section">
                    <h3 className="category-heading">
                      {category === "Roadside Assistance" && <FaTools className="category-icon" />}
                      {category === "Vehicle Recovery" && <FaTruck className="category-icon" />}
                      {category === "Motorcycle Recovery" && <FaMotorcycle className="category-icon" />}
                      {category === "Vehicle Transportation" && <FaRoad className="category-icon" />}
                      {category}
                    </h3>

                    <div className="services-grid">
                      {categoryServices.map((service) => {
                        const isSelected = selectedServices.find((s) => s.id === service.id);
                        return (
                          <label key={service.id} className={`service-checkbox-card ${isSelected ? "selected" : ""}`}>
                            <input
                              type="checkbox"
                              checked={!!isSelected}
                              onChange={() => handleToggle(service)}
                            />
                            <div className="checkbox-custom">
                              <FaCheckCircle className="check-icon" />
                            </div>
                            <div className="serv-info">
                              <span className="serv-name">{service.name}</span>
                              <p className="serv-description">{service.description}</p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div className="summary-sidebar">
                <div className="summary-card">
                  <h3 className="summary-title">
                    <FaCheckCircle className="summary-icon" />
                    Selected Services
                  </h3>

                  {selectedServices.length === 0 ? (
                    <div className="empty-state">
                      <p>No services selected yet</p>
                      <span>Select recovery services to continue</span>
                    </div>
                  ) : (
                    <>
                      <div className="selected-services-list">
                        {selectedServices.map((service) => (
                          <div key={service.id} className="selected-service-item">
                            <div className="service-detail">
                              <span className="item-name">{service.name}</span>
                              <span className="item-category">{service.category}</span>
                            </div>
                            <button className="remove-btn" onClick={() => handleToggle(service)} aria-label="Remove service">
                              ×
                            </button>
                          </div>
                        ))}
                      </div>

                      <div className="summary-divider"></div>

                      <div className="selection-summary">
                        <div className="summary-row">
                          <span>Total Services</span>
                          <span className="summary-value">{selectedServices.length}</span>
                        </div>
                      </div>

                      <button className="cta-button-recovery" onClick={handleRequestRecovery}>
                        <span>Request Recovery Service</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </button>

                      <div className="info-note">
                        <p>Our team will contact you with pricing and availability for immediate recovery assistance.</p>
                      </div>
                    </>
                  )}
                </div>

                <div className="contact-quick-card">
                  <h4>Emergency Breakdown?</h4>
                  <p>Call us now for immediate assistance</p>
                  <a href="tel:+447501911970" className="phone-link-recovery">
                    📞 +447501911970
                  </a>

                  {/* internal link fixed */}
                  <Link to="/contact" className="contact-link-recovery">
                    Contact Us
                  </Link>
                </div>

              </div>
            </div>
          </div>
        </section>

        <section className="recovery-info-section">
          <div className="recovery-container">
            <div className="infoss-grid">
              <div className="infoss-card">
                <div className="infoss-icon">
                  <FaRoad />
                </div>
                <h3>24/7 Availability</h3>
                <p>Round-the-clock recovery service for emergencies and breakdowns anytime, anywhere.</p>
              </div>
              <div className="infoss-card">
                <div className="infoss-icon">
                  <FaTruck />
                </div>
                <h3>Professional Equipment</h3>
                <p>Modern recovery trucks and equipment for safe vehicle transportation.</p>
              </div>
              <div className="infoss-card">
                <div className="infoss-icon">
                  <FaCheckCircle />
                </div>
                <h3>Fully Insured</h3>
                <p>Comprehensive insurance coverage for complete peace of mind during recovery.</p>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Recovery;
